/**
 * Google Fonts API Service
 * Handles fetching and caching Google Fonts data
 */

export interface GoogleFont {
  family: string;
  variants: string[];
  subsets: string[];
  category: string;
  version: string;
  lastModified: string;
  files: Record<string, string>;
}

export interface GoogleFontsApiResponse {
  kind: string;
  items: GoogleFont[];
}

export interface FontCategory {
  id: string;
  label: string;
  icon: string;
}

export const FONT_CATEGORIES: FontCategory[] = [
  { id: 'all', label: 'googleFonts.categories.all', icon: 'fas fa-font' },
  { id: 'sans-serif', label: 'googleFonts.categories.sansSerif', icon: 'fas fa-font' },
  { id: 'serif', label: 'googleFonts.categories.serif', icon: 'fas fa-italic' },
  { id: 'display', label: 'googleFonts.categories.display', icon: 'fas fa-heading' },
  { id: 'handwriting', label: 'googleFonts.categories.handwriting', icon: 'fas fa-pen-fancy' },
  { id: 'monospace', label: 'googleFonts.categories.monospace', icon: 'fas fa-code' },
];

// Web-safe fonts that work in all email clients
export const EMAIL_SAFE_FONTS = [
  { family: 'Arial', category: 'sans-serif', fallback: 'Helvetica, sans-serif' },
  { family: 'Arial Black', category: 'sans-serif', fallback: 'Gadget, sans-serif' },
  { family: 'Comic Sans MS', category: 'handwriting', fallback: 'cursive' },
  { family: 'Courier New', category: 'monospace', fallback: 'Courier, monospace' },
  { family: 'Georgia', category: 'serif', fallback: 'serif' },
  { family: 'Impact', category: 'sans-serif', fallback: 'Charcoal, sans-serif' },
  { family: 'Lucida Console', category: 'monospace', fallback: 'Monaco, monospace' },
  { family: 'Lucida Sans Unicode', category: 'sans-serif', fallback: 'Lucida Grande, sans-serif' },
  { family: 'Palatino Linotype', category: 'serif', fallback: 'Book Antiqua, Palatino, serif' },
  { family: 'Tahoma', category: 'sans-serif', fallback: 'Geneva, sans-serif' },
  { family: 'Times New Roman', category: 'serif', fallback: 'Times, serif' },
  { family: 'Trebuchet MS', category: 'sans-serif', fallback: 'Helvetica, sans-serif' },
  { family: 'Verdana', category: 'sans-serif', fallback: 'Geneva, sans-serif' },
];

const STORAGE_KEYS = {
  cache: 'spm_google_fonts_cache',
  favorites: 'spm_google_fonts_favorites',
  recent: 'spm_google_fonts_recent',
};

const CACHE_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds
const MAX_RECENT_FONTS = 10;

// Icon fonts to exclude from the list (not text fonts)
const ICON_FONTS_PATTERNS = [
  'Material Icons',
  'Material Symbols',
  'Noto Color Emoji',
  'Noto Emoji',
  'Font Awesome',
  'Icons',
];

// Google Fonts API key - should be set in environment
const API_KEY = process.env.VUE_APP_GOOGLE_FONTS_API_KEY || '';

class GoogleFontsService {
  private fonts: GoogleFont[] = [];
  private isLoading = false;
  private loadPromise: Promise<GoogleFont[]> | null = null;

  /**
   * Fetch fonts from Google Fonts API with caching
   */
  async fetchFonts(): Promise<GoogleFont[]> {
    // Return cached fonts if available
    if (this.fonts.length > 0) {
      return this.fonts;
    }

    // If already loading, wait for the existing promise
    if (this.loadPromise) {
      return this.loadPromise;
    }

    // Check localStorage cache
    const cached = this.getCachedFonts();
    if (cached) {
      this.fonts = cached;
      return this.fonts;
    }

    // Fetch from API
    this.isLoading = true;
    this.loadPromise = this.fetchFromApi();

    try {
      this.fonts = await this.loadPromise;
      this.cacheFonts(this.fonts);
      return this.fonts;
    } finally {
      this.isLoading = false;
      this.loadPromise = null;
    }
  }

  /**
   * Fetch fonts directly from Google Fonts API
   */
  private async fetchFromApi(): Promise<GoogleFont[]> {
    try {
      const url = API_KEY
        ? `https://www.googleapis.com/webfonts/v1/webfonts?key=${API_KEY}&sort=popularity`
        : 'https://www.googleapis.com/webfonts/v1/webfonts?sort=popularity';

      const response = await fetch(url);

      if (!response.ok) {
        console.error('[GoogleFontsService] API error:', response.status);
        return this.getFallbackFonts();
      }

      const data: GoogleFontsApiResponse = await response.json();
      // Filter out icon fonts
      const textFonts = (data.items || []).filter((font) => !this.isIconFont(font.family));
      return textFonts;
    } catch (error) {
      console.error('[GoogleFontsService] Fetch error:', error);
      return this.getFallbackFonts();
    }
  }

  /**
   * Check if font is an icon font (not suitable for text)
   */
  private isIconFont(fontFamily: string): boolean {
    return ICON_FONTS_PATTERNS.some((pattern) => fontFamily.toLowerCase().includes(pattern.toLowerCase()));
  }

  /**
   * Get fonts from localStorage cache
   */
  private getCachedFonts(): GoogleFont[] | null {
    try {
      const cached = localStorage.getItem(STORAGE_KEYS.cache);
      if (!cached) return null;

      const { data, timestamp } = JSON.parse(cached);
      const isExpired = Date.now() - timestamp > CACHE_DURATION;

      if (isExpired) {
        localStorage.removeItem(STORAGE_KEYS.cache);
        return null;
      }

      return data;
    } catch {
      return null;
    }
  }

  /**
   * Cache fonts to localStorage
   */
  private cacheFonts(fonts: GoogleFont[]): void {
    try {
      const cacheData = {
        data: fonts,
        timestamp: Date.now(),
      };
      localStorage.setItem(STORAGE_KEYS.cache, JSON.stringify(cacheData));
    } catch (error) {
      console.warn('[GoogleFontsService] Cache storage failed:', error);
    }
  }

  /**
   * Fallback fonts list if API fails
   */
  private getFallbackFonts(): GoogleFont[] {
    return [
      { family: 'Roboto', category: 'sans-serif', variants: ['regular', 'bold'], subsets: ['latin'], version: 'v30', lastModified: '', files: {} },
      { family: 'Open Sans', category: 'sans-serif', variants: ['regular', 'bold'], subsets: ['latin'], version: 'v35', lastModified: '', files: {} },
      { family: 'Lato', category: 'sans-serif', variants: ['regular', 'bold'], subsets: ['latin'], version: 'v24', lastModified: '', files: {} },
      { family: 'Montserrat', category: 'sans-serif', variants: ['regular', 'bold'], subsets: ['latin'], version: 'v26', lastModified: '', files: {} },
      { family: 'Poppins', category: 'sans-serif', variants: ['regular', 'bold'], subsets: ['latin'], version: 'v20', lastModified: '', files: {} },
      { family: 'Oswald', category: 'sans-serif', variants: ['regular', 'bold'], subsets: ['latin'], version: 'v53', lastModified: '', files: {} },
      { family: 'Raleway', category: 'sans-serif', variants: ['regular', 'bold'], subsets: ['latin'], version: 'v29', lastModified: '', files: {} },
      { family: 'Playfair Display', category: 'serif', variants: ['regular', 'bold'], subsets: ['latin'], version: 'v30', lastModified: '', files: {} },
      { family: 'Merriweather', category: 'serif', variants: ['regular', 'bold'], subsets: ['latin'], version: 'v30', lastModified: '', files: {} },
      { family: 'Nunito', category: 'sans-serif', variants: ['regular', 'bold'], subsets: ['latin'], version: 'v26', lastModified: '', files: {} },
    ];
  }

  /**
   * Search fonts by name
   */
  searchFonts(query: string): GoogleFont[] {
    if (!query.trim()) return this.fonts;

    const lowerQuery = query.toLowerCase();
    return this.fonts.filter((font) => font.family.toLowerCase().includes(lowerQuery));
  }

  /**
   * Get fonts by category
   */
  getFontsByCategory(category: string): GoogleFont[] {
    if (category === 'all') return this.fonts;
    return this.fonts.filter((font) => font.category === category);
  }

  /**
   * Get favorite fonts from localStorage
   */
  getFavorites(): string[] {
    try {
      const favorites = localStorage.getItem(STORAGE_KEYS.favorites);
      return favorites ? JSON.parse(favorites) : [];
    } catch {
      return [];
    }
  }

  /**
   * Add font to favorites
   */
  addFavorite(fontFamily: string): void {
    const favorites = this.getFavorites();
    if (!favorites.includes(fontFamily)) {
      favorites.push(fontFamily);
      localStorage.setItem(STORAGE_KEYS.favorites, JSON.stringify(favorites));
    }
  }

  /**
   * Remove font from favorites
   */
  removeFavorite(fontFamily: string): void {
    const favorites = this.getFavorites().filter((f) => f !== fontFamily);
    localStorage.setItem(STORAGE_KEYS.favorites, JSON.stringify(favorites));
  }

  /**
   * Check if font is favorite
   */
  isFavorite(fontFamily: string): boolean {
    return this.getFavorites().includes(fontFamily);
  }

  /**
   * Get recent fonts from localStorage
   */
  getRecentFonts(): string[] {
    try {
      const recent = localStorage.getItem(STORAGE_KEYS.recent);
      return recent ? JSON.parse(recent) : [];
    } catch {
      return [];
    }
  }

  /**
   * Add font to recent list
   */
  addRecentFont(fontFamily: string): void {
    let recent = this.getRecentFonts().filter((f) => f !== fontFamily);
    recent.unshift(fontFamily);
    recent = recent.slice(0, MAX_RECENT_FONTS);
    localStorage.setItem(STORAGE_KEYS.recent, JSON.stringify(recent));
  }

  /**
   * Generate Google Fonts CSS link URL
   */
  generateFontLink(fontFamily: string, weights: string[] = ['400', '700']): string {
    const family = fontFamily.replace(/ /g, '+');
    const weightsStr = weights.join(';');
    return `https://fonts.googleapis.com/css2?family=${family}:wght@${weightsStr}&display=swap`;
  }

  /**
   * Check if a font is email-safe
   */
  isEmailSafe(fontFamily: string): boolean {
    return EMAIL_SAFE_FONTS.some((f) => f.family.toLowerCase() === fontFamily.toLowerCase());
  }

  /**
   * Get email-safe font data
   */
  getEmailSafeFontData(fontFamily: string): typeof EMAIL_SAFE_FONTS[0] | undefined {
    return EMAIL_SAFE_FONTS.find((f) => f.family.toLowerCase() === fontFamily.toLowerCase());
  }

  /**
   * Clear all cached data
   */
  clearCache(): void {
    localStorage.removeItem(STORAGE_KEYS.cache);
    this.fonts = [];
  }

  /**
   * Extract Google Fonts used in a document by scanning font-family styles
   */
  extractGoogleFontsFromDocument(doc: Document): string[] {
    const foundFonts = new Set<string>();


    // Get all elements with inline font-family style
    const allElements = doc.querySelectorAll('*');

    allElements.forEach((el) => {
      const element = el as HTMLElement;
      const fontFamily = element.style.fontFamily;
      if (fontFamily) {
        this.extractFontNamesFromValue(fontFamily).forEach((font) => foundFonts.add(font));
      }
    });

    // Also check computed styles in style elements
    const styleElements = doc.querySelectorAll('style');

    styleElements.forEach((styleEl, index) => {
      const cssText = styleEl.textContent || '';
      const fontFamilyMatches = cssText.match(/font-family\s*:\s*([^;]+)/gi);
      if (fontFamilyMatches) {
        fontFamilyMatches.forEach((match) => {
          const value = match.replace(/font-family\s*:\s*/i, '').trim();
          this.extractFontNamesFromValue(value).forEach((font) => foundFonts.add(font));
        });
      }
    });


    // Filter to only Google Fonts (not web-safe)
    const googleFonts = Array.from(foundFonts).filter((font) => {
      const isEmailSafe = this.isEmailSafe(font);
      const isWebSafe = this.isWebSafeFont(font);
      return !isEmailSafe && !isWebSafe;
    });

    return googleFonts;
  }

  /**
   * Extract font names from a font-family CSS value
   */
  private extractFontNamesFromValue(fontFamilyValue: string): string[] {
    const fonts: string[] = [];
    // Match quoted font names or unquoted single-word names
    const matches = fontFamilyValue.match(/"([^"]+)"|'([^']+)'|([^,\s]+)/g);
    if (matches) {
      matches.forEach((match) => {
        // Remove quotes and trim
        const fontName = match.replace(/["']/g, '').trim();
        // Skip generic CSS font families and categories
        const genericFamilies = [
          'serif', 'sans-serif', 'monospace', 'cursive', 'fantasy', 'system-ui',
          'ui-serif', 'ui-sans-serif', 'ui-monospace', 'ui-rounded',
          'handwriting', 'display', 'inherit', 'initial', 'unset',
        ];
        if (!genericFamilies.includes(fontName.toLowerCase())) {
          fonts.push(fontName);
        }
      });
    }
    return fonts;
  }

  /**
   * Check if font is a common web-safe font
   */
  private isWebSafeFont(fontFamily: string): boolean {
    const webSafeFonts = [
      'arial', 'helvetica', 'times new roman', 'times', 'courier new', 'courier',
      'verdana', 'georgia', 'palatino', 'garamond', 'bookman', 'comic sans ms',
      'trebuchet ms', 'arial black', 'impact', 'tahoma', 'geneva', 'lucida',
      'lucida sans', 'lucida grande', 'lucida console', 'monaco',
    ];
    return webSafeFonts.includes(fontFamily.toLowerCase());
  }
}

// Singleton instance
export const googleFontsService = new GoogleFontsService();
export default googleFontsService;
